import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { FaSave, FaHourglassHalf } from 'react-icons/fa';
import { useTheme } from '../ThemeContext';
import StaffStatus from './StaffStatus';

// Global Admin → Stripe Billing. Platform-level Stripe configuration for native
// Pipsqueak Pro purchases (in-app Stripe Checkout).
function BillingSettings() {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [billing, setBilling] = useState({
    enabled: false,
    has_secret_key: false,
    has_webhook_secret: false,
    masked_secret_key: '',
    publishable_key: '',
    price_id: ''
  });
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  // The Pro trial new workspaces start with. Its own field and Save: it does not depend
  // on Stripe being configured, and saving it must not resubmit the Stripe keys.
  const [trialDays, setTrialDays] = useState('15');
  const [savingTrial, setSavingTrial] = useState(false);

  const adminToken = localStorage.getItem('Admintoken');

  const loadBilling = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/s1/admin/billing-settings', {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to load billing settings');
      if (data.settings) {
        setBilling(data.settings);
        if (data.settings.pro_trial_days !== undefined) setTrialDays(String(data.settings.pro_trial_days));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBilling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveBilling = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s1/admin/billing-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          enabled: billing.enabled,
          publishableKey: billing.publishable_key || '',
          priceId: billing.price_id || '',
          // Only send secrets when the admin typed a new value (blank = keep existing).
          secretKey: secretKey.trim() || undefined,
          webhookSecret: webhookSecret.trim() || undefined
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save billing settings');
      setBilling(data.settings);
      setSecretKey('');
      setWebhookSecret('');
      setMessage('Stripe billing settings saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const trialValue = Number(trialDays);
  const trialValid = trialDays !== '' && Number.isInteger(trialValue) && trialValue >= 0 && trialValue <= 365;

  const saveTrial = async () => {
    if (!trialValid) return;
    setSavingTrial(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch('/s1/admin/billing-settings/trial', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ proTrialDays: trialValue })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to save the trial length');
      if (data.settings) setTrialDays(String(data.settings.pro_trial_days));
      setMessage(data.message || 'Trial length saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingTrial(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className={isDarkMode ? 'text-light' : ''}>
      <StaffStatus on={billing.enabled && billing.has_secret_key && billing.price_id}>
        {/* About Stripe only — the trial works whether or not checkout is set up. */}
        Stripe checkout: {billing.enabled && billing.has_secret_key && billing.price_id ? 'Enabled' : 'Disabled'}
      </StaffStatus>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}

      <Card className={`mb-4 ${isDarkMode ? 'bg-dark text-light border-secondary' : ''}`}>
        <Card.Body>
          <h5 className="mb-1"><FaHourglassHalf className="me-2" /> Pro trial</h5>
          <p className={isDarkMode ? 'text-light opacity-75 mb-3' : 'text-muted mb-3'}>
            Every new workspace — self sign-up or created in Tenant Management — starts on Pro for
            this many days, then moves to Community unless it upgrades. The trial is per workspace:
            everyone in it shares one trial, and a workspace never gets a second one.
          </p>
          <Row className="g-3 align-items-end">
            <Col xs={12} sm={6} md={4}>
              <Form.Group controlId="pro-trial-days">
                <Form.Label>Trial length (days)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={365}
                  step={1}
                  value={trialDays}
                  isInvalid={!trialValid}
                  onChange={(e) => setTrialDays(e.target.value)}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
                <Form.Control.Feedback type="invalid">A whole number from 0 to 365.</Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs="auto">
              <Button onClick={saveTrial} disabled={savingTrial || !trialValid}>
                {savingTrial ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
                Save Trial Length
              </Button>
            </Col>
          </Row>
          <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
            {trialValid && trialValue === 0
              ? 'Trials are off: new workspaces start on Community.'
              : 'Applies to workspaces created from now on. Trials already running keep their end date. Set 0 to turn trials off.'}
          </Form.Text>
        </Card.Body>
      </Card>

      <Card className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Card.Body>
          <Row className="g-4">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Secret Key</Form.Label>
                <Form.Control
                  type="password"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder={billing.has_secret_key ? `Saved (${billing.masked_secret_key || '••••'}). Enter a new key to replace it.` : 'sk_live_… or sk_test_…'}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
                <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                  Stored encrypted server-side. Never returned to the browser.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Price ID (recurring)</Form.Label>
                <Form.Control
                  value={billing.price_id || ''}
                  onChange={(e) => setBilling(prev => ({ ...prev, price_id: e.target.value }))}
                  placeholder="price_…"
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
                <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                  The Stripe Price for the Pipsqueak Pro subscription.
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Publishable Key (optional)</Form.Label>
                <Form.Control
                  value={billing.publishable_key || ''}
                  onChange={(e) => setBilling(prev => ({ ...prev, publishable_key: e.target.value }))}
                  placeholder="pk_live_… or pk_test_…"
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Webhook Signing Secret</Form.Label>
                <Form.Control
                  type="password"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder={billing.has_webhook_secret ? 'Saved. Enter a new value to replace it.' : 'whsec_…'}
                  className={isDarkMode ? 'bg-dark text-light border-secondary' : ''}
                />
                <Form.Text className={isDarkMode ? 'text-light opacity-75' : 'text-muted'}>
                  From your Stripe webhook endpoint pointing to <code>/s1/api/billing/webhook/stripe</code> (events: checkout.session.completed, invoice.paid, customer.subscription.deleted).
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Check
                type="switch"
                id="stripe-billing-enabled"
                label="Enable in-app Stripe checkout for Pipsqueak Pro"
                checked={!!billing.enabled}
                onChange={(e) => setBilling(prev => ({ ...prev, enabled: e.target.checked }))}
              />
            </Col>
          </Row>

          <div className="d-flex gap-2 mt-4">
            <Button onClick={saveBilling} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
              Save Billing Settings
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}

export default BillingSettings;
